<script setup lang="ts">
    import { Validation } from '~/data/Validation'

    const qty = ref('1')
    const variants = ref({})
    const addingToCart = ref(false)
    const error = ref(false)
    const form = ref()

    const slug = currentSlug()
    const { data: product, refresh } =
        await useStatamicEntries().getEntryBySlug<Product>({
            collection: 'products',
            value: slug,
        })

    // default to the first value for each variation
    product.value.product_variants?.variants.forEach(
        (variant) => (variants.value[variant.name] = variant.values[0]),
    )

    useStatamicPreview(refresh)

    useHead({
        bodyAttrs: {
            class: {
                [`product product-${slug}`]: true,
                'with-feature-image': computed(
                    () => product.value?.feature_image != null,
                ),
            },
        },
        title: () => product.value?.title ?? 'Product',
    })

    const addToCart = async () => {
        console.log('addToCart', product)
        addingToCart.value = true
        error.value = false

        try {
            await useSimpleCommerceCart().addProduct(
                product.value,
                parseInt(qty.value),
                variants.value['Fabric Colour'],
            )
        } catch (err) {
            console.error('err!!!', err)
            addingToCart.value = false
            error.value = true
            return
        }

        addingToCart.value = false
        navigateTo('/quote/cart')
    }
</script>

<template>
    <div class="page">
        <PageHeader
            :image="product.feature_image"
            :content="product.intro ?? ''"
            :faded="true"
            fit="contain"
            class="!mb-0"
        >
            <NuxtLink to="/shop">
                Back to
                <span class="icon-arrow-northwest whitespace-nowrap">Shop</span>
            </NuxtLink>
        </PageHeader>

        <div class="gutter-x block-gap-b sm:layout-grid">
            <h1
                class="mb-12 mt-8 pl-0 font-bold uppercase sm:col-span-4 sm:col-start-1 sm:pr-4 md:col-span-9 md:col-start-2 md:pr-0 lg:col-span-9 lg:col-start-3 lg:mb-20 xl:col-span-9 xl:col-start-3 xl:mb-24 2xl:col-span-5 2xl:col-start-4"
            >
                {{ product.title }}
            </h1>

            <div
                class="pl-0 text-[22px] sm:col-span-4 sm:col-start-1 sm:pr-4 md:col-span-9 md:col-start-2 md:pr-0 lg:col-span-7 lg:col-start-3 xl:col-span-7 xl:col-start-3 2xl:col-span-5 2xl:col-start-4"
            >
                <div class="raw-content" v-html="product.description" />

                <form
                    ref="form"
                    class="mt-12 flex flex-col flex-wrap gap-8 border-t pt-4 lg:mt-20 xl:mt-24 xl:flex-row"
                    @submit.prevent="addToCart"
                >
                    <div class="flex-1">
                        <strong>Price (ex GST)</strong>
                        <ProductPrice :product="product" class="text-3xl" />
                    </div>

                    <div class="flex-1">
                        <strong>Quantity</strong>

                        <ProductQuantity :product="product" v-model="qty" />

                        <ul
                            v-if="product.product_variants"
                            v-for="variation in product.product_variants
                                .variants"
                            :key="variation.name"
                        >
                            <li class="pt-1">
                                <strong>{{ variation.name }}</strong>
                                <InputSelect
                                    :field="{
                                        display: '',
                                        width: 100,
                                        type: 'select',
                                        handle: `variations${variation.name}]`,
                                        options:
                                            product.product_variants.options.map(
                                                (option) => ({
                                                    key: option.key,
                                                    value: option.key,
                                                    image: option.swatch?.url,
                                                }),
                                            ),
                                        validate: [Validation.required],
                                    }"
                                    v-model="variants[variation.name]"
                                    class="product-select !my-0"
                                    required
                                />
                            </li>
                        </ul>
                    </div>

                    <PrettyButton
                        type="submit"
                        :loading="addingToCart"
                        class="w-full text-xl md:text-2xl lg:text-3xl"
                    >
                        Request for Quote
                    </PrettyButton>

                    <p
                        v-if="error"
                        class="w-full text-center text-sm font-bold uppercase"
                    >
                        Unable to add product to your quote. Please try again.
                    </p>
                </form>
            </div>

            <ul
                v-if="product.details.length > 0"
                class="mt-8 border-t border-grey pt-4 sm:col-span-2 sm:ml-4 sm:mt-0 md:col-span-4 md:col-start-12 md:ml-0 lg:col-start-11 xl:col-span-3 xl:col-start-12 2xl:col-span-2 2xl:col-start-11"
            >
                <li v-for="(detail, i) in product.details" :key="detail.id">
                    <div
                        class="uppercase"
                        data-aos="fade-up"
                        :data-aos-delay="50 * i"
                        data-aos-offset="-100"
                    >
                        {{ detail.detail }}
                    </div>

                    <div
                        class="mb-4 text-grey"
                        data-aos="fade-up"
                        :data-aos-delay="100 * i"
                        data-aos-offset="-100"
                    >
                        {{ detail.info }}
                    </div>
                </li>
            </ul>
        </div>

        <Blocks v-if="product.blocks" :blocks="product.blocks" />
    </div>
</template>

<style scoped>
    form strong {
        font-weight: bold;
        font-size: 1rem;
        text-transform: uppercase;
    }
</style>
